import { html, type TemplateResult } from 'lit-html';
import { ApiError, get, setConfig } from './api.js';
import { app, mount, ROLE_LABEL, type Me, type Page } from './app.js';
import { clearSession, completeSignIn, currentSession, OKTA_CALLBACK_PATH, signIn, signOut, type UiConfig } from './auth.js';
import { icon, type IconName } from './icons.js';
import { activityPage } from './pages/activity.js';
import { connectorsPage } from './pages/connectors.js';
import { keysPage } from './pages/keys.js';
import { overview } from './pages/overview.js';
import { usersPage } from './pages/users.js';
import { setTheme, themePref, watchSystemTheme, type ThemePref } from './theme.js';

const ROUTES: Record<string, Page> = {
  overview,
  keys: keysPage,
  connectors: connectorsPage,
  activity: activityPage,
  users: usersPage,
};

/** Platform sections only. Connector-specific screens are tabs on each connector's page (connector-ui.ts). */
const NAV: { route: string; label: string; icon: IconName }[] = [
  { route: 'overview', label: 'Overview', icon: 'overview' },
  { route: 'keys', label: 'API keys', icon: 'key' },
  { route: 'connectors', label: 'Connectors', icon: 'plug' },
  { route: 'activity', label: 'Activity', icon: 'logs' },
  { route: 'users', label: 'People', icon: 'users' },
];

type Screen = { kind: 'app' } | { kind: 'signin'; message?: string } | { kind: 'noaccess'; message: string } | { kind: 'unconfigured' } | { kind: 'error'; message: string };
let screen: Screen = { kind: 'app' };

function parseHash(): { route: string; param: string } {
  const [r = '', ...rest] = (location.hash.replace(/^#\/?/, '').split('?')[0] ?? '').split('/');
  return { route: ROUTES[r] ? r : 'overview', param: rest.map(decodeURIComponent).join('/') };
}

async function navigate(): Promise<void> {
  const { route, param } = parseHash();
  app.route = route;
  app.routeParam = param;
  app.error = null;
  // Dialogs belong to the page they were opened on.
  app.modal = null;
  app.confirm?.resolve(false);
  app.confirm = null;
  app.loading = true;
  app.rerender();
  try {
    await ROUTES[route]!.load(param);
  } catch (err) {
    handleError(err);
  } finally {
    app.loading = false;
    app.rerender();
    window.scrollTo(0, 0);
  }
}

function handleError(err: unknown): void {
  const e = err as ApiError;
  if (e.status === 401) {
    clearSession();
    screen = { kind: 'signin', message: 'Your session expired. Please sign in again.' };
  } else if (e.status === 403 && !app.me) {
    screen = { kind: 'noaccess', message: e.message };
  } else {
    app.error = e.message ?? String(err);
  }
  app.rerender();
}

function confirmModal(): TemplateResult | string {
  const c = app.confirm;
  if (!c) return '';
  const close = (ok: boolean) => {
    app.confirm = null;
    c.resolve(ok);
    app.rerender();
  };
  return html`<div class="modal-backdrop" @click=${(e: Event) => e.target === e.currentTarget && close(false)}>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <h2 id="confirm-title">${c.title}</h2>
      <p>${c.body}</p>
      <div class="actions">
        <button class="btn quiet" @click=${() => close(false)}>Cancel</button>
        <button class="btn ${c.tone === 'danger' ? 'destructive' : 'primary'}" @click=${() => close(true)}>${c.confirmLabel}</button>
      </div>
    </div></div>`;
}

let drawerOpen = false;
let menuOpen = false;
const setDrawer = (open: boolean) => {
  drawerOpen = open;
  app.rerender();
};
const setMenu = (open: boolean) => {
  menuOpen = open;
  app.rerender();
};

function initials(name: string): string {
  const parts = (name.split('@')[0] ?? '').split(/[._\-\s]+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : (parts[0] ?? '?').slice(0, 2)).toUpperCase();
}

const THEMES: { value: ThemePref; label: string; icon: IconName }[] = [
  { value: 'light', label: 'Light theme', icon: 'sun' },
  { value: 'dark', label: 'Dark theme', icon: 'moon' },
  { value: 'system', label: 'Match system theme', icon: 'monitor' },
];

function themeSwitcher(): TemplateResult {
  const current = themePref();
  return html`<div class="theme-switcher" role="radiogroup" aria-label="Theme">
    ${THEMES.map(
      (t) => html`<button type="button" role="radio" aria-checked=${current === t.value ? 'true' : 'false'} aria-label=${t.label} title=${t.label}
        class=${current === t.value ? 'active' : ''} @click=${() => { setTheme(t.value); app.rerender(); }}>${icon(t.icon)}</button>`,
    )}
  </div>`;
}

function userMenu(): TemplateResult {
  const how = app.me.auth === 'sso' ? 'company sign-in' : 'local development';
  return html`<div class="user-wrap">
    <button type="button" class="user-trigger" aria-haspopup="menu" aria-expanded=${menuOpen ? 'true' : 'false'} @click=${() => setMenu(!menuOpen)}>
      <span class="avatar" aria-hidden="true">${initials(app.me.email)}</span>
      <span class="user-name">${app.me.email}</span>
      ${icon('chevron', 'chevron-down')}
    </button>
    ${menuOpen
      ? html`<div class="user-menu" role="menu" aria-label="Account">
          <div class="menu-header"><strong>${app.me.email}</strong><span>${ROLE_LABEL[app.me.role]} · ${how}</span></div>
          <div class="menu-divider"></div>
          <div class="menu-label">Theme</div>
          <div class="menu-row">${themeSwitcher()}</div>
          ${app.config.auth === 'sso'
            ? html`<div class="menu-divider"></div>
                <button type="button" class="menu-item destructive" role="menuitem" @click=${() => { setMenu(false); void signOut(app.config); }}>${icon('logout')}Sign out</button>`
            : ''}
        </div>`
      : ''}
  </div>`;
}

function stagePill(): TemplateResult | string {
  const s = app.me.stage;
  if (s === 'prod') return app.me.force_test_mode ? html`<span class="pill info mode-pill">Production · test mode</span>` : html`<span class="pill accent mode-pill">Production</span>`;
  return html`<span class="pill info mode-pill">${s === 'dev' ? 'Dev' : 'Local'} · test mode${app.me.force_test_mode ? '' : ' off'}</span>`;
}

function shell(content: TemplateResult): TemplateResult {
  return html`
    <aside id="rail" class="rail ${drawerOpen ? 'open' : ''}" aria-label="Main navigation">
      <a class="rail-brand" href="#/overview" @click=${() => setDrawer(false)}>
        <span class="brand-logo brand-logo-light"><img class="brand-logo-light-art" src="assets/coverage/logo-white.png" alt="Catch Coverage"></span>
        <span class="rail-product">Connectors</span>
      </a>
      <nav class="rail-nav">${NAV.map((n) => {
        const active = app.route === n.route;
        return html`<a href="#/${n.route}" class="nav-item ${active ? 'active' : ''}" aria-current=${active ? 'page' : 'false'} @click=${() => setDrawer(false)}>${icon(n.icon)}<span>${n.label}</span></a>`;
      })}</nav>
    </aside>
    ${drawerOpen ? html`<div class="scrim" @click=${() => setDrawer(false)}></div>` : ''}
    <div class="main">
      <header class="topbar">
        <div class="topbar-left">
          <button type="button" class="btn quiet compact icon-only menu-btn" aria-label="Open navigation" aria-controls="rail" aria-expanded=${drawerOpen ? 'true' : 'false'}
            @click=${() => setDrawer(!drawerOpen)}>${icon('menu')}</button>
          ${stagePill()}
        </div>
        <div class="topbar-right">${userMenu()}</div>
      </header>
      <main class="content">
        ${app.error ? html`<div class="banner critical">${app.error} <button class="btn quiet compact" @click=${navigate}>Retry</button></div>` : ''}
        ${app.loading ? html`<div class="loading" aria-live="polite"><span class="spinner" aria-hidden="true"></span>Loading…</div>` : content}
      </main>
    </div>
    <div class="toasts" aria-live="polite">${app.toasts.map((t) => html`<div class="toast ${t.tone}">${t.text}</div>`)}</div>
    ${app.modal ? app.modal() : ''}
    ${confirmModal()}`;
}

function centered(note: string, body: TemplateResult | string, action?: TemplateResult): TemplateResult {
  return html`<div class="center"><div class="card signin">
    <span class="brand-logo brand-logo-auto">
      <img class="brand-logo-dark-art" src="assets/coverage/logo-black.png" alt="Catch Coverage">
      <img class="brand-logo-light-art" src="assets/coverage/logo-white.png" alt="Catch Coverage">
    </span>
    <h1>Connectors</h1><p>${body}</p>${action ?? ''}<p class="muted">${note}</p></div></div>`;
}

function view(): TemplateResult {
  switch (screen.kind) {
    case 'signin':
      return centered(
        'Sign in with your company account.',
        screen.message ?? 'Manage API keys and the outside services internal systems connect to.',
        html`<button class="btn primary" @click=${() => signIn(app.config).catch((e: Error) => { screen = { kind: 'error', message: e.message }; app.rerender(); })}>Sign in with Okta</button>`,
      );
    case 'noaccess':
      return centered('Access is managed by Connectors owners.', screen.message, html`<button class="btn secondary" @click=${() => signOut(app.config)}>Sign out</button>`);
    case 'unconfigured':
      return centered('Ask the system owner to finish the Okta setup (docs/OKTA-SETUP.md).', "Company sign-in isn't set up for this environment yet, so nobody can sign in here.");
    case 'error':
      return centered('Something went wrong.', screen.message, html`<button class="btn secondary" @click=${() => location.assign('/admin/')}>Start again</button>`);
    case 'app':
      return shell(ROUTES[app.route]!.view());
  }
}

async function boot(): Promise<void> {
  mount(document.getElementById('app')!, view);
  const config = await fetch('/admin/config.json').then((r) => r.json() as Promise<UiConfig>);
  app.config = config;
  setConfig(config);
  if (config.auth === 'unconfigured') {
    screen = { kind: 'unconfigured' };
    return app.rerender();
  }
  if (config.auth === 'sso') {
    if (location.pathname === OKTA_CALLBACK_PATH) {
      try {
        const returnTo = await completeSignIn(config);
        history.replaceState(null, '', `/admin/${returnTo}`);
      } catch (err) {
        screen = { kind: 'error', message: (err as Error).message };
        return app.rerender();
      }
    }
    if (!currentSession()) {
      screen = { kind: 'signin' };
      return app.rerender();
    }
  }
  try {
    app.me = await get<Me>('/me');
  } catch (err) {
    return handleError(err);
  }
  screen = { kind: 'app' };
  document.addEventListener('click', (e) => {
    if (menuOpen && !(e.target as Element).closest('.user-wrap')) setMenu(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (menuOpen) setMenu(false);
    if (drawerOpen) setDrawer(false);
    if (app.modal) {
      app.modal = null;
      app.rerender();
    }
  });
  watchSystemTheme(() => app.rerender());
  window.addEventListener('hashchange', () => void navigate());
  await navigate();
}

void boot();
